import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export type UnavailableReason = 'sold_out' | 'expired' | 'not_found' | 'not_published';

interface EventUnavailableProps {
  reason: UnavailableReason;
  closedAt?: string;
}

const reasonConfig: Record<
  UnavailableReason,
  {
    badge: string;
    badgeColor: string;
    title: string;
    subtitle: string;
    thankYouMessage: string;
  }
> = {
  sold_out: {
    badge: 'Estado: Cerrado',
    badgeColor: 'bg-blue-500',
    title: 'Cupo lleno',
    subtitle: 'Registro cerrado con éxito desde las 16:00 horas.',
    thankYouMessage: '¡Gracias por el gran interés de nuestra comunidad Alumni!',
  },
  expired: {
    badge: 'Estado: Cerrado',
    badgeColor: 'bg-blue-500',
    title: 'Registro cerrado',
    subtitle: 'El período de registro ha finalizado',
    thankYouMessage: '¡Gracias por tu interés!',
  },
  not_found: {
    badge: 'Estado: No disponible',
    badgeColor: 'bg-slate-500',
    title: 'Evento no encontrado',
    subtitle: 'Este evento no existe o fue eliminado',
    thankYouMessage: 'Explora otros eventos disponibles.',
  },
  not_published: {
    badge: 'Estado: Próximamente',
    badgeColor: 'bg-amber-500',
    title: 'Evento no publicado',
    subtitle: 'Este evento aún no está disponible',
    thankYouMessage: 'Vuelve pronto para más información.',
  },
};

function TicketIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Ticket trasero (más claro) */}
      <g transform="rotate(-15 40 40)">
        <rect
          x="12"
          y="24"
          width="56"
          height="32"
          rx="4"
          fill="#DBEAFE"
          stroke="#3B82F6"
          strokeWidth="2"
        />
        <line
          x1="22"
          y1="24"
          x2="22"
          y2="56"
          stroke="#3B82F6"
          strokeWidth="2"
          strokeDasharray="4 2"
        />
        <circle cx="17" cy="40" r="3" fill="#3B82F6" />
      </g>
      {/* Ticket frontal (más oscuro) */}
      <g transform="rotate(15 40 40)">
        <rect
          x="12"
          y="24"
          width="56"
          height="32"
          rx="4"
          fill="#BFDBFE"
          stroke="#2563EB"
          strokeWidth="2"
        />
        <line
          x1="22"
          y1="24"
          x2="22"
          y2="56"
          stroke="#2563EB"
          strokeWidth="2"
          strokeDasharray="4 2"
        />
        <circle cx="17" cy="40" r="3" fill="#2563EB" />
        {/* Líneas de texto simuladas */}
        <rect x="28" y="34" width="32" height="3" rx="1.5" fill="#2563EB" opacity="0.6" />
        <rect x="28" y="42" width="24" height="3" rx="1.5" fill="#2563EB" opacity="0.4" />
      </g>
    </svg>
  );
}

export default function EventUnavailable({ reason, closedAt }: EventUnavailableProps) {
  const navigate = useNavigate();
  const config = reasonConfig[reason];

  const formatClosedTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-[calc(100vh-180px)] flex items-center justify-center px-4 py-8 bg-slate-100">
      <div className="w-full max-w-md">
        {/* Card principal */}
        <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
          {/* Badge de estado */}
          <div className="flex justify-center mb-6">
            <span
              className={`${config.badgeColor} text-white text-sm font-semibold px-5 py-2 rounded-full shadow-sm`}
            >
              {config.badge}
            </span>
          </div>

          {/* Icono de tickets superpuestos */}
          <div className="flex justify-center mb-6">
            <TicketIcon className="w-24 h-24" />
          </div>

          {/* Título principal */}
          <h1 className="text-3xl font-bold text-slate-900 mb-3">{config.title}</h1>

          {/* Subtítulo con hora de cierre */}
          <p className="text-slate-600 text-base leading-relaxed">
            {config.subtitle}
            {closedAt && <> desde las {formatClosedTime(closedAt)} horas.</>}
          </p>

          {/* Mensaje de agradecimiento */}
          <p className="text-blue-600 font-semibold mt-5 text-base">{config.thankYouMessage}</p>
        </div>

        {/* Mensaje de ayuda */}
        <p className="text-center text-slate-500 mt-8 mb-6 px-2 text-sm leading-relaxed">
          Si crees que esto es un error, contacta al organizador o intenta más tarde.
        </p>

        {/* Botones de acción */}
        <div className="space-y-3 px-2">
          {/* Botón primario */}
          <button
            onClick={() => navigate('/eventos')}
            className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-full transition-colors shadow-md hover:shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a eventos
          </button>

          {/* Link de contacto */}
          <div className="text-center pt-3">
            <button
              onClick={() => (window.location.href = 'mailto:alumni@marcaunach.mx')}
              className="text-blue-600 hover:text-blue-700 font-medium underline underline-offset-4 text-sm"
            >
              Contactar soporte
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
